import { ReactNode, useCallback, useEffect, useMemo, useState } from "react";

import Box from "@mui/material/Box";
import CircularProgress from "@mui/material/CircularProgress";
import IconButton from "@mui/material/IconButton";
import Stack from "@mui/material/Stack";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";

import { colord } from "colord";
import { useAnchoring } from "hooks";
import { get } from "lodash";

import { misc } from "app/themes/colors";
import { SelectorIcon } from "components/icons";
import EnumDataSource from "utils/enum-data-source.util";

import StatusIconContainer from "./status-icon-container.component";
import StatusIconPlaceholder from "./status-icon-placeholder.component";
import { getTransitionsRules } from "./status-transition-cell.utils";
import StatusTransitionMenu from "./status-transition-menu.component";

export type StatusTransitionCode = string | number;
export type DisabledStatusTransitionCode = Record<StatusTransitionCode, string>;

export type StatusTransitionFlow = Record<
  StatusTransitionCode,
  StatusTransitionCode[]
>;

export type DisabledStatusTransitionFlow = Record<
  StatusTransitionCode,
  DisabledStatusTransitionCode
>;

export type StatusTransition = {
  value: StatusTransitionCode;
  label: string;
};

type StatusTransitionCellProps = {
  testId?: string;
  readonly?: boolean;
  status: StatusTransitionCode;
  statusColors?: Record<StatusTransitionCode, string>;
  statusIcons?: Record<StatusTransitionCode, ReactNode>;
  allStatuses: EnumDataSource;
  showIconOnly?: boolean;
  statusTransitions?: StatusTransitionFlow;
  disabledStatusTransitions?: DisabledStatusTransitionFlow;
  design?: "rounded" | "squared";
  onChange?: (code: StatusTransitionCode) => Promise<any>;
  onClick?: () => void;
  onBlur?: () => void;
  isLoading?: boolean;
  onMenuOpen?: (isOpen: boolean) => void;
  noStatusLabel?: string;
  excludeStatuses?: StatusTransitionCode[];
  overrideCurrentColor?: string;
  overrideCurrentIcon?: ReactNode;
  overrideCurrentText?: string;
  overrideTooltipText?: string;
  isSpaceBetween?: boolean;
  isNoWrap?: boolean;
  minWidth?: string;
  width?: string;
  secondaryAction?: { label: string; onClick: () => void };
};
export default function StatusTransitionCell({
  testId = "status-transition-cell",
  readonly = false,
  status = "",
  statusColors,
  statusIcons,
  allStatuses,
  statusTransitions,
  disabledStatusTransitions,
  onBlur,
  onChange,
  onClick,
  design = "rounded",
  isLoading,
  showIconOnly,
  onMenuOpen,
  noStatusLabel,
  excludeStatuses = [],
  overrideCurrentColor,
  overrideCurrentIcon,
  overrideCurrentText,
  overrideTooltipText,
  isSpaceBetween,
  isNoWrap,
  minWidth,
  width,
  secondaryAction
}: StatusTransitionCellProps) {
  const [isUpdating, setIsUpdating] = useState(false);
  const { anchorEl, handleClick, handleClose } = useAnchoring(onBlur);

  const transitionRules = useMemo(
    () =>
      getTransitionsRules(
        status,
        allStatuses,
        statusTransitions,
        excludeStatuses
      ),
    [allStatuses, excludeStatuses, status, statusTransitions]
  );

  const allowTransitions = !readonly && transitionRules[status || ""]?.length;
  const currentStatusColor = overrideCurrentColor
    ? overrideCurrentColor
    : get(statusColors, status, misc.darkYellow);
  const currentStatusIcon = overrideCurrentIcon
    ? overrideCurrentIcon
    : get(statusIcons, status, null);
  const handleChange = useCallback(
    async (code: StatusTransitionCode) => {
      setIsUpdating(true);
      try {
        await onChange?.(code);
      } finally {
        setIsUpdating(false);
      }
    },
    [setIsUpdating, onChange]
  );

  useEffect(() => {
    onMenuOpen?.(Boolean(anchorEl));
  }, [anchorEl, onMenuOpen]);

  const availableTransitions = useMemo(
    () => transitionRules[status || ""],
    [status, transitionRules]
  );

  const disabledTransitions = useMemo(
    () => disabledStatusTransitions?.[status],
    [disabledStatusTransitions, status]
  );

  return (
    <Box width={width || "100%"} minWidth={minWidth}>
      <Stack
        data-testid={testId}
        direction="row"
        alignItems="center"
        justifyContent={!isSpaceBetween ? "center" : "space-between"}
        gap={0.7}
        borderRadius={design === "rounded" ? 24 : "3px"}
        minHeight={28}
        minWidth={28}
        boxSizing="border-box"
        width="100%"
        bgcolor={colord(currentStatusColor).alpha(0.1).saturate(0.1).toHex()}
        sx={{
          cursor: readonly ? "unset" : "pointer",
          px: 0.75,
          py: 0.25
        }}
        onClick={(e) => {
          if (!isLoading && Boolean(allowTransitions)) {
            handleClick(e);
            e.stopPropagation();
          } else if (onClick) {
            onClick();
          }
        }}
      >
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="center"
          gap={0.7}
          minHeight={28}
          minWidth={28}
          boxSizing="border-box"
          pl={!isSpaceBetween ? 0 : 1}
        >
          <Tooltip
            title={
              showIconOnly
                ? (overrideTooltipText ?? allStatuses.getFromCode(status))
                : (overrideTooltipText ?? "")
            }
          >
            <span>
              {currentStatusIcon ? (
                <StatusIconContainer>{currentStatusIcon}</StatusIconContainer>
              ) : (
                <StatusIconPlaceholder
                  sx={{ borderRadius: design === "rounded" ? "2px" : "50%" }}
                  color={currentStatusColor}
                />
              )}
            </span>
          </Tooltip>

          {!showIconOnly && (
            <Typography
              variant="body1"
              sx={{
                textTransform: "uppercase",
                fontSize: "12px",
                lineHeight: "15px",
                pb: "2px",
                whiteSpace: isNoWrap ? "nowrap" : undefined
              }}
            >
              {overrideCurrentText
                ? overrideCurrentText
                : allStatuses.getFromCode(status) || noStatusLabel || ""}
            </Typography>
          )}
        </Stack>

        {Boolean(allowTransitions) && (
          <IconButton
            data-testid={`${testId}-selector`}
            sx={{ p: 0.25, pl: 0 }}
          >
            <SelectorIcon />
            {isUpdating && <CircularProgress size={20} />}
          </IconButton>
        )}
      </Stack>
      {secondaryAction && (
        <Typography
          data-testid={`${testId}-secondary-action`}
          variant="caption"
          onClick={(e) => {
            e.stopPropagation();
            secondaryAction.onClick();
          }}
          sx={{
            display: "block",
            mt: 0.4,
            textAlign: "center",
            fontSize: "11px",
            color: "primary.main",
            textDecoration: "underline",
            cursor: "pointer"
          }}
        >
          {secondaryAction.label}
        </Typography>
      )}
      {Boolean(allowTransitions) && (
        <StatusTransitionMenu
          testId={testId}
          anchorEl={anchorEl}
          status={status}
          statuses={allStatuses}
          statusColors={statusColors}
          statusIcons={statusIcons}
          availableTransitions={availableTransitions}
          disabledTransitions={disabledTransitions}
          onChange={handleChange}
          onClose={handleClose}
        />
      )}
    </Box>
  );
}
